import { truncated } from "@flatfile/design-system";
import { BoxShadowBorder, Border } from "../../common/UI/Borders.js";
import { FlexCenter } from "../../common/UI/Flex.js";
import { ZIndex } from "../../common/UI/ZIndex.js";
import { CELL_TOOLTIP_CLASS } from "../Table/constants.js";
import { SelectedIndexCellStyle } from "../../hooks/useSelectCells.js";
import { named } from "../../utils/named.js";
import styled, { css } from "styled-components";
const DisableSelection = `
  ::selection {
    background: none;
  }

  * ::selection {
    background: none;
  }
`;
const getCellBorder = (props) => {
  const color = {
    color: props.isIndexColumn ? props.theme.indexColumn.borderColor : props.isFirstRow ? props.theme.column.header.borderColor : props.theme.cell.borderColor
  };
  let bottomRight = `border-bottom: ${Border.small(color)};`;
  if (props.isLastRow && props.pinned) {
    bottomRight = `box-shadow: ${BoxShadowBorder.bottomRight(color)};`;
  } else if (props.pinned) {
    bottomRight = `
      box-shadow: ${BoxShadowBorder.right(color)};
      border-bottom: ${Border.small(color)};
    `;
  } else if (props.isLastRow) {
    if (props.isLastColumn) {
      bottomRight = `box-shadow: ${BoxShadowBorder.bottomRight(color)};`;
    } else {
      bottomRight = `box-shadow: ${BoxShadowBorder.bottom(color)};`;
    }
  } else if (props.isLastColumn) {
    bottomRight = `
      box-shadow: ${BoxShadowBorder.right(color)};
      border-bottom: ${Border.small(color)};
    `;
  }
  return props.isFirstColumn ? `${bottomRight}` : `${bottomRight}
    border-left: ${Border.small(color)}`;
};
const SharedCellStyles = css`
  ${FlexCenter}
  color: ${(props) => props.theme.cell.color};
  font-size: ${(props) => props.theme.baseFontSize};
  line-height: inherit;
  padding: 3px;
  box-sizing: border-box;
  font-family: ${(props) => props.theme.fontFamily};
  user-select: none;

  &:hover {
    cursor: default;
  }
`;
const PinnedIndexCellStyles = css`
  position: sticky;
  z-index: ${ZIndex.level600};
`;
const ErrorCellStyles = css`
  background: ${(props) => props.theme.cell.error.backgroundColor};
  color: ${(props) => props.theme.cell.error.color};
`;
const WarningCellStyles = css`
  background: ${(props) => props.theme.cell.warning.backgroundColor};
  color: ${(props) => props.theme.cell.warning.color};
`;
const DisabledCellStyles = css`
  background: ${(props) => props.theme.cell.disabled.backgroundColor};
  color: ${(props) => props.theme.cell.disabled.color};
`;
const NumberCellStyles = css`
  justify-content: flex-end;
  text-align: right;
  font-family: ${(props) => props.theme.cell.number.fontFamily};
`;
const UnderlinedCellStyles = css`
  text-decoration: underline;
  text-decoration-style: dashed;
  padding-bottom: 2px;
  text-decoration-color: ${(props) => props.theme.cell.mutatedUnderlineColor};
  text-underline-offset: 4px;
`;
const TooltipContainerStyles = (props) => css`
  border-radius: ${(props2) => props2.theme.tooltip.borderRadius};
  box-shadow: ${(styledProps) => !styledProps.theme.cell.active.boxShadow && props.noShadow ? "none" : styledProps.theme.cell.active.boxShadow};
  border: ${(props2) => Border.status(props2)};
`;
const OutlinePositioning = (props) => {
  const showTop = !props.openUpwards;
  const showBottom = props.openUpwards || props.openUpwards === void 0;
  return css`
    position: absolute;
    right: ${props.isLastColumn ? "0px" : "-1px"};
    left: ${props.isFirstColumn ? "1px" : "-1px"};
    ${showTop && `top: ${props.isFirstNonHeaderRow ? "0px" : "-1px"};`}
    ${showBottom && `bottom: ${props.isLastRow ? "0px" : "-1px"};`}
  `;
};
const FocusOutline = (focusOutlineProps) => css`
  &:after {
    content: '';
    ${OutlinePositioning}
    z-index: ${ZIndex.level200};
    border: ${(props) => Border.status({ ...props, ...focusOutlineProps })};
    border-radius: ${(props) => props.theme.tooltip.borderRadius};
    box-shadow: ${(props) => props.theme.cell.active.boxShadow};
  }
`;
const BasicCellWrapper = styled.div`
  ${SharedCellStyles}
  ${(props) => getCellBorder({
  isLastRow: props.isLastRow,
  isLastColumn: props.isLastColumn,
  isFirstColumn: props.isFirstColumn,
  pinned: props.pinned,
  theme: props.theme
})}
  background: ${(props) => props.theme.backgroundColor};
  &.cell-ai-generated {
    background-color: ${(props) => props.theme.column.autoGeneratedBackgroundColor};
  }
  ${(props) => (!props.isEditing || props.disableSelection) && DisableSelection}
  ${(props) => props.disabled && DisabledCellStyles}
  ${(props) => props.showFocus && FocusOutline(props)}
  ${(props) => props.isFirstColumn && "padding-left: 4px;"}

  .hidden-content {
    opacity: 0;
  }

  &:focus {
    outline: none;
    ${(props) => props.isFocused && FocusOutline(props)}
  }

  &:hover {
    .hidden-content {
      opacity: 1;
    }

    > div:first-child {
      z-index: ${ZIndex.level300};
    }

    > .${CELL_TOOLTIP_CLASS} {
      display: block;
    }

    ${(props) => props.hasTooltip && `
      &:after {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      }
    `}
  }
`;
named("BasicCellWrapper", BasicCellWrapper);
const BasicCellInner = styled.div`
  ${FlexCenter}
  ${(props) => props.error && ErrorCellStyles}
  ${(props) => props.warning && WarningCellStyles}
  border-radius: ${(props) => ` ${props.theme.cell.borderRadius}`};
  max-width: 100%;
  width: 100%;
  height: 100%;
  padding: 1px 5px;
  position: relative;
  ${(props) => props.boolean ? `
    gap: 12px;
  ` : `
    justify-content: space-between;
  `}

  ${(props) => props.hidden && "opacity: 0;"}
  ${(props) => props.number && NumberCellStyles}
`;
const Label = styled.span`
  ${truncated}
  ${(props) => props.secondary && `color: ${props.theme.cell.secondaryColor};`}
  ${(props) => props.mutated && UnderlinedCellStyles}
  ${(props) => props.added && ValueAddedStyles}
  ${(props) => props.removed && ValueRemovedStyles}
  ${(props) => props.requiresDecision && ValueRequiresDecisionStyles}
`;
const IndexCellWrapper = styled.div`
  ${SharedCellStyles}
  ${(props) => getCellBorder({
  isLastRow: props.isLastRow,
  isLastColumn: props.isLastColumn,
  isFirstColumn: props.isFirstColumn,
  isFirstRow: props.isFirstRow,
  isIndexColumn: props.isIndexColumn,
  pinned: props.pinned,
  theme: props.theme
})}
  ${DisableSelection}
  ${(props) => props.pinned && PinnedIndexCellStyles}
  ${(props) => (props.hovered || props.selected) && `z-index: ${ZIndex.level700};`}
  ${(props) => props.pinned && props.isFirstRow && "top: 0;"}
  ${(props) => props.isIndexColumn && props.isFirstRow && css`
      z-index: ${ZIndex.level800};
      top: 0;
    `}

  ${(props) => props.isFirstColumn && css`
      background: ${props.theme.indexColumn.backgroundColor};
      color: ${props.theme.indexColumn.color};
      font-weight: ${props.theme.indexColumn.fontWeight};
      font-size: ${props.theme.indexColumn.fontSize};
      font-family: ${props.theme.indexColumn.fontFamily};
    `}

  ${(props) => props.isFirstRow && css`
      font-weight: 400;
      font-size: ${props.theme.column.header.fontSize};
    `}

  ${(props) => props.isIndexColumn && !props.isFirstRow && css`
      &:hover {
        ${SelectedIndexCellStyle(props.theme)}

        .checkbox {
          display: flex;
        }
      }
    `}

  flex-shrink: 0;

  > span {
    ${DisableSelection}
  }
`;
const TooltipWrapper = styled.div`
  ${TooltipContainerStyles}
  ${OutlinePositioning}
  width: ${(props) => {
  if (props.isFirstColumn)
    return `100%`;
  if (props.isLastColumn)
    return `calc(100% + 1px)`;
  return `calc(100% + 2px)`;
}};
  min-height: ${(props) => props.theme.rowHeight + 1}px;
  z-index: ${ZIndex.level300};
  display: none;
  overflow: hidden;
  pointer-events: none;
`;
const interpolatePadding = (rowHeight, min, max, minPad, maxPad) => {
  const slope = (maxPad - minPad) / (max - min);
  const padding = minPad + slope * (rowHeight - min);
  return Math.max(minPad, Math.min(maxPad, padding));
};
const getUpwardsBottomPadding = (rowHeight) => interpolatePadding(rowHeight, 25, 35, 3, 8.5);
const getDownwardsBottomPadding = (rowHeight) => interpolatePadding(rowHeight, 25, 35, 4, 9.5);
const getMessageTopPadding = (rowHeight) => interpolatePadding(rowHeight, 25, 35, 4.5, 9);
const TooltipInner = styled.div`
  ${(props) => props.openUpwards ? css`
          margin-bottom: ${props.theme.rowHeight}px;
          box-shadow: ${BoxShadowBorder.bottom()};
          padding-bottom: ${getUpwardsBottomPadding(props.theme.rowHeight)}px;
          padding-top: 1px;
        ` : css`
          margin-top: ${props.isFirstNonHeaderRow ? props.theme.rowHeight - 1 : props.theme.rowHeight}px;
          box-shadow: ${BoxShadowBorder.top()};
          padding-bottom: ${getDownwardsBottomPadding(props.theme.rowHeight)}px;
        `}
  background: ${(props) => props.theme.tooltip.backgroundColor};
  color: ${(props) => props.theme.tooltip.color};
  font-size: 11px;
  position: relative;

  &:hover {
    display: none;
  }
`;
const TooltipMessage = styled.div`
  padding-top: ${(props) => getMessageTopPadding(props.theme.rowHeight)}px;
  padding-right: 8px;
  padding-left: 8px;
  line-height: 16px;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ meta }) => meta && `font-style: italic;`}
`;
const DropdownIcon = styled.div`
  ${FlexCenter}
  justify-content: center;
  position: relative;
  padding: 0 3px;
`;
const EditorContainerWrapper = styled.div`
  ${OutlinePositioning}
  z-index: ${ZIndex.level500};
  min-height: ${(props) => props.isFirstNonHeaderRow || props.isLastRow ? props.theme.rowHeight : props.theme.rowHeight + 1}px;
`;
styled.span`
  width: 4px;
  flex-shrink: 0;
`;
const ValueRemovedStyles = css`
  text-decoration: line-through;
  overflow: hidden;

  line-height: 24px;

  &:last-child {
    margin-right: auto;
    margin-left: 4px;
  }

  &,
  div {
    color: ${({ theme }) => theme.cell.removedColor} !important;
  }
`;
const ValueRemovedMessageStyles = css`
  overflow: hidden;

  &,
  div {
    color: ${({ theme }) => theme.cell.removedColor} !important;
    ${({ $empty }) => $empty && "font-style: italic;"}
  }
`;
const SharedLabelsWithBackgroundsStyles = css`
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 24px;
  letter-spacing: 0em;
  text-align: left;
  display: inline-block;
  padding: 0 4px;
  margin-left: -4px;

  &:last-child:not(:first-child) {
    margin-right: auto;
    margin-left: 4px;
  }
`;
const ValueRequiresDecisionStyles = css`
  ${SharedLabelsWithBackgroundsStyles}

  &,
  div {
    color: var(--colors-amber-12);
    background-color: var(--colors-amber-9);
    ${({ $empty }) => $empty && "font-style: italic;"}
  }
`;
const ValueAddedStyles = css`
  ${SharedLabelsWithBackgroundsStyles}

  &:last-child {
    margin-right: auto;
  }

  &,
  div {
    color: ${({ theme }) => theme.cell.addedColor} !important;
    background-color: ${({ theme }) => theme.cell.addedBackgroundColor};
    ${({ $empty }) => $empty && "font-style: italic;"}
  }
`;
const ValueAddedPillsStyles = css`
  &:nth-last-child(2) {
    margin-right: auto;
  }
  & > div > div {
    font-weight: 600;
    background-color: ${({ theme }) => theme.cell.addedBackgroundColor};
  }
  & > div > div > div {
    color: ${({ theme }) => theme.cell.addedColor};
  }
`;
const ValueRequiresDecisionPillsStyles = css`
  &:nth-last-child(2) {
    margin-right: auto;
  }
  & > div > div {
    font-weight: 600;
    background-color: var(--colors-amber-9);
  }
  & > div > div > div {
    color: var(--colors-amber-12);
  }
`;
const ValueRemoved = styled.span`
  ${ValueRemovedStyles}
`;
const ValueRemovedMessage = styled.span`
  ${ValueRemovedMessageStyles}
`;
const ValueRequiresDecision = styled.span`
  ${ValueRequiresDecisionStyles}
`;
const ValueRequiresDecisionPills = styled.span`
  ${ValueRequiresDecisionPillsStyles}
`;
const ValueAdded = styled.span`
  ${ValueAddedStyles}
`;
const ValueAddedPills = styled.span`
  ${ValueAddedPillsStyles}
`;
export {
  BasicCellInner,
  BasicCellWrapper,
  DisableSelection,
  DropdownIcon,
  EditorContainerWrapper,
  IndexCellWrapper,
  Label,
  NumberCellStyles,
  TooltipContainerStyles,
  TooltipInner,
  TooltipMessage,
  TooltipWrapper,
  UnderlinedCellStyles,
  ValueAdded,
  ValueAddedPills,
  ValueRemoved,
  ValueRemovedMessage,
  ValueRequiresDecision,
  ValueRequiresDecisionPills
};
